import {
  featureFlagsActions,
  featureFlagsSelectors,
  featureFlagsState,
  FeatureFlag,
} from '@/lib/store/feature-flags.store';
import { logger } from '@/lib/utils/logger';
import { apiService } from './api.service'; // Assuming you have a central API service
import { API_CONFIG } from '@/lib/config/api.config'; // Assuming API endpoint config
import { Session } from 'next-auth';

// Configuration for local storage caching
const LOCAL_STORAGE_KEY = 'featureFlagsCache';
const CACHE_TTL_MS = 5 * 60 * 1000; // Cache time-to-live: 5 minutes

/**
 * Interface for the data structure stored in local storage.
 */
interface CachedFlags {
  flags: Record<string, FeatureFlag>;
  timestamp: number;
}

/**
 * Interface defining the expected structure of the API response
 * when fetching all feature flags.
 */
interface ApiFeatureFlagsResponse {
  flags: Record<
    string,
    {
      enabled: boolean;
      description?: string | null;
    }
  >;
}

/**
 * Represents the expected shape of the feature flag DTO returned by the backend Admin API.
 */
type AdminFeatureFlagDto = FeatureFlag & {
  name: string;
  description?: string | null;
};

/**
 * Service layer for managing feature flags.
 * Handles fetching flags from the API, caching them in local storage,
 * and providing methods to interact with the feature flag state via the store.
 * Implements stale-while-revalidate caching for non-development environments.
 */
class FeatureFlagsService {
  private isInitializing = false;
  private isDevelopment = process.env.NODE_ENV === 'development'; // Check environment

  /**
   * Reads the feature flag cache from local storage.
   * @returns The cached data or null if not found or invalid.
   */
  private getCachedFlags(): CachedFlags | null {
    try {
      const cachedData = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!cachedData) return null;

      const parsedCache: CachedFlags = JSON.parse(cachedData);
      // Basic validation of the cache structure
      if (
        typeof parsedCache === 'object' &&
        parsedCache !== null &&
        typeof parsedCache.flags === 'object' &&
        typeof parsedCache.timestamp === 'number'
      ) {
        return parsedCache;
      }
      logger.warn(
        '[FeatureFlagsService] Invalid cache structure found in local storage.'
      );
      this.clearCache(); // Clear invalid cache
      return null;
    } catch (error) {
      logger.warn(
        '[FeatureFlagsService] Failed to read or parse local storage cache.',
        error
      );
      this.clearCache(); // Clear potentially corrupted cache
      return null;
    }
  }

  /**
   * Writes the current feature flags to the local storage cache.
   * @param flags - The flags object to cache.
   */
  private setCachedFlags(flags: Record<string, FeatureFlag>): void {
    try {
      const cacheToStore: CachedFlags = {
        flags: flags,
        timestamp: Date.now(),
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cacheToStore));
      logger.debug(
        '[FeatureFlagsService] Saved feature flags to local storage cache.'
      );
    } catch (error) {
      logger.warn(
        '[FeatureFlagsService] Failed to save flags to local storage.',
        error
      );
    }
  }

  /**
   * Removes the feature flag cache from local storage.
   */
  private clearCache(): void {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      logger.debug('[FeatureFlagsService] Cleared local storage cache.');
    } catch (error) {
      logger.warn(
        '[FeatureFlagsService] Failed to remove local storage cache item.',
        error
      );
    }
  }

  /**
   * Fetches flags from the API, updates the store, and updates the cache.
   * Designed to run in the background without affecting the main initialization flow's loading state.
   * @param session - The user's current session, required for API calls.
   */
  private async fetchAndUpdateInBackground(
    session: Session | null
  ): Promise<void> {
    if (!session) {
      logger.warn(
        '[FeatureFlagsService Background] No session provided, cannot fetch flags.'
      );
      // Optionally clear cache/flags here if needed, but maybe not desirable for background task
      return;
    }

    logger.debug(
      '[FeatureFlagsService Background] Starting background fetch...'
    );
    try {
      const response = await apiService.get<ApiFeatureFlagsResponse>(
        API_CONFIG.ENDPOINTS.FEATURE_FLAGS.GET_ALL,
        session
      );

      if (response?.flags) {
        const flagsForStore: Record<string, FeatureFlag> = {};
        for (const key in response.flags) {
          flagsForStore[key] = { enabled: response.flags[key].enabled, description: response.flags[key].description };
        }
        // Update the store with fresh data
        featureFlagsActions.setFlags({ flags: flagsForStore });
        // Update the cache
        this.setCachedFlags(flagsForStore);
        logger.info(
          '[FeatureFlagsService Background] Background update successful.',
          {
            count: Object.keys(flagsForStore).length,
          }
        );
        // Clear any previous *global* error state after a successful background fetch
        if (featureFlagsState.error.peek()) {
          featureFlagsState.error.set(null);
        }
      } else {
        logger.warn(
          '[FeatureFlagsService Background] Invalid API response format.'
        );
        // Don't throw an error here, just log, as it's a background task
      }
    } catch (error: any) {
      logger.error(
        '[FeatureFlagsService Background] Failed to fetch/update flags.',
        error
      );
      // Don't set global error state here for background task, just log it.
    }
  }

  /**
   * Initializes the feature flag store using a stale-while-revalidate strategy for non-dev environments.
   * - Always loads from cache immediately if available.
   * - If cache is stale (older than 5 mins) in non-dev, triggers a background fetch.
   * - If cache is stale in dev (or missing), performs a foreground fetch.
   * Requires a valid session for API calls.
   *
   * @param session - The user's current session, required for API calls.
   * @returns A promise that resolves when the initial setup (cache load or foreground fetch) is complete.
   */
  async initializeFlags(session: Session | null): Promise<void> {
    if (this.isInitializing) {
      logger.debug(
        '[FeatureFlagsService] Initialization already in progress, skipping.'
      );
      return;
    }

    const currentState = featureFlagsState.get();
    // Skip if already loaded and not in an error state (error state might warrant a retry)
    if (
      !currentState.isLoading &&
      Object.keys(currentState.flags).length > 0 &&
      !currentState.error
    ) {
      logger.debug(
        '[FeatureFlagsService] Flags already initialized and loaded, skipping.'
      );
      return;
    }

    this.isInitializing = true;
    // Assume loading initially, will be set false quickly if loading from cache
    featureFlagsState.isLoading.set(true);
    featureFlagsState.error.set(null); // Clear previous errors on new init attempt

    logger.info(
      `[FeatureFlagsService] Initializing feature flags... (Dev: ${this.isDevelopment})`
    );

    const cachedData = this.getCachedFlags();
    const now = Date.now();
    const isCacheStale =
      !cachedData || now - (cachedData?.timestamp || 0) >= CACHE_TTL_MS;

    let performImmediateFetch = false;
    let triggerBackgroundFetch = false;

    // 1. Load from cache if it exists
    if (cachedData) {
      logger.info(
        `[FeatureFlagsService] Loading flags from cache (Stale: ${isCacheStale}).`
      );
      featureFlagsActions.setFlags({ flags: cachedData.flags });
      featureFlagsState.isLoading.set(false); // Set loading false after cache load

      // Decide if background fetch is needed (non-dev, stale cache)
      if (isCacheStale && !this.isDevelopment) {
        triggerBackgroundFetch = true;
      }
    }

    // 2. Decide if immediate fetch is needed
    // Needed if: No cache OR (stale cache AND in development)
    if (!cachedData || (isCacheStale && this.isDevelopment)) {
      performImmediateFetch = true;
      // Ensure loading remains true if we need to fetch immediately
      if (!featureFlagsState.isLoading.peek()) {
        featureFlagsState.isLoading.set(true);
      }
      logger.info(
        `[FeatureFlagsService] Cache ${!cachedData ? 'missing' : 'stale (dev mode)'}. Performing immediate fetch.`
      );
    }

    // 3. Execute background fetch if needed (fire and forget)
    if (triggerBackgroundFetch) {
      logger.info(
        '[FeatureFlagsService] Triggering background fetch for stale cache.'
      );
      this.fetchAndUpdateInBackground(session); // Don't await
    }

    // 4. Execute immediate fetch if needed
    if (performImmediateFetch) {
      if (!session) {
        logger.warn(
          '[FeatureFlagsService] Immediate fetch required but no session provided. Clearing state.'
        );
        featureFlagsActions.setFlags({ flags: {} });
        this.clearCache();
        featureFlagsState.error.set(
          new Error('User session is required to fetch feature flags.')
        );
        featureFlagsState.isLoading.set(false);
        this.isInitializing = false;
        return;
      }

      logger.debug('[FeatureFlagsService] Starting immediate API fetch...');
      try {
        // Fetch, update store, update cache (similar to background fetch but awaited)
        const response = await apiService.get<ApiFeatureFlagsResponse>(
          API_CONFIG.ENDPOINTS.FEATURE_FLAGS.GET_ALL,
          session
        );

        if (response?.flags) {
          const flagsForStore: Record<string, FeatureFlag> = {};
          for (const key in response.flags) {
            flagsForStore[key] = { enabled: response.flags[key].enabled, description: response.flags[key].description };
          }
          featureFlagsActions.setFlags({ flags: flagsForStore });
          this.setCachedFlags(flagsForStore);
          featureFlagsState.error.set(null); // Clear error on success
          logger.info('[FeatureFlagsService] Immediate fetch successful.', {
            count: Object.keys(flagsForStore).length,
          });
        } else {
          logger.error(
            '[FeatureFlagsService] Invalid API response during immediate fetch.'
          );
          throw new Error('Invalid API response format');
        }
      } catch (error: any) {
        logger.error(
          '[FeatureFlagsService] Failed immediate API fetch.',
          error
        );
        // Don't clear flags here, the cache (if loaded) might be better than nothing
        // Clear cache only if the fetch failed? Maybe not, keep stale cache? Let's keep it.
        // this.clearCache();
        featureFlagsState.error.set(
          error instanceof Error ? error : new Error('Failed immediate fetch')
        );
        // Let potential stale flags from cache remain
        // featureFlagsActions.setFlags({ flags: {} });
      } finally {
        featureFlagsState.isLoading.set(false);
      }
    }

    // 5. Finalize initialization state
    this.isInitializing = false;
    // If neither immediate nor background fetch happened, isLoading was already set false by cache load.
    // If immediate fetch happened, isLoading is set false in its finally block.
    logger.debug('[FeatureFlagsService] Initialization process complete.');
  }

  /**
   * Checks if a specific feature flag is enabled by querying the store.
   * Note: Assumes `initializeFlags` has been called previously.
   *
   * @param flagName - The name of the feature flag.
   * @returns True if the feature is enabled according to the store, false otherwise.
   */
  isFeatureEnabled(flagName: string): boolean {
    return featureFlagsSelectors.isFeatureEnabled(flagName);
  }

  /**
   * Gets the entire map of feature flags from the store.
   * Note: Assumes `initializeFlags` has been called previously.
   *
   * @returns A record of all feature flags and their states from the store.
   */
  getAllFlags(): Record<string, FeatureFlag> {
    return featureFlagsSelectors.getAllFlags();
  }

  /**
   * Gets the loading state from the store.
   * @returns True if flags are currently being loaded or initialized.
   */
  isLoading(): boolean {
    return featureFlagsSelectors.isLoading();
  }

  /**
   * Gets any error that occurred during flag loading/initialization from the store.
   * @returns An Error object if an error occurred, otherwise null.
   */
  getError(): Error | null {
    return featureFlagsSelectors.getError();
  }

  /**
   * Creates a new feature flag on the server via the Admin API.
   * Requires a valid session with admin privileges.
   * Invalidates the local storage cache on successful creation.
   *
   * @param flagData - Object containing the name, optional description, and optional enabled state.
   * @param session - The user's session object (must have admin rights).
   * @returns The newly created flag data from the API response.
   * @throws Will throw an error if the API call fails (e.g., conflict, validation error).
   */
  async createFlag(
    flagData: { name: string; description?: string; enabled?: boolean },
    session: Session
  ): Promise<FeatureFlag & { name: string; description?: string | null }> {
    // Return type matches backend DTO
    logger.debug(
      `[FeatureFlagsService] Requesting creation for flag '${flagData.name}' via Admin API.`,
      flagData
    );
    try {
      const response = await apiService.post<AdminFeatureFlagDto>(
        API_CONFIG.ENDPOINTS.FEATURE_FLAGS.ADMIN_CREATE,
        flagData,
        session,
      );

      if (!response) {
         throw new Error("API did not return the created flag data.");
      }

      logger.info(
        `[FeatureFlagsService] Successfully created flag '${flagData.name}' on the server. Invalidating cache.`
      );

      // Invalidate cache after successful server creation
      this.clearCache();

      // Return the newly created flag data
      return response;
    } catch (error) {
      logger.error(
        `[FeatureFlagsService] Failed to create flag '${flagData.name}' on server via Admin API.`,
        error
      );
      // Re-throw the error for the store action to handle
      throw error;
    }
  }

  /**
   * Updates a specific feature flag's properties (enabled, description) on the server via the Admin API.
   * Requires a valid session with admin privileges.
   * Invalidates the local storage cache on successful update.
   *
   * @param flagName - The name of the feature flag to update.
   * @param updateData - An object containing the properties to update (e.g., { enabled: boolean, description?: string }).
   * @param session - The user's session object (must have admin rights).
   * @returns The updated flag data from the API response.
   * @throws Will throw an error if the API call fails.
   */
  async updateFlag(
    { flagName, updateData }: { flagName: string; updateData: { enabled?: boolean; description?: string } },
    session: Session
  ): Promise<FeatureFlag & { name: string; description?: string | null }> {
    // Return type matches backend DTO
    logger.debug(
      `[FeatureFlagsService] Requesting update for flag '${flagName}' via Admin API.`,
      updateData
    );
    try {
      const response = await apiService.patch<AdminFeatureFlagDto>(
        API_CONFIG.ENDPOINTS.FEATURE_FLAGS.ADMIN_UPDATE(flagName),
        updateData,
        session,
      );

      if (!response) {
        throw new Error("API did not return the updated flag data.");
      }

      logger.info(
        `[FeatureFlagsService] Successfully updated flag '${flagName}' on the server. Invalidating cache.`
      );

      // Invalidate cache after successful server update
      this.clearCache();

      // Return the updated flag data
      return response;
    } catch (error) {
      logger.error(
        `[FeatureFlagsService] Failed to update flag '${flagName}' on server via Admin API.`,
        error
      );
      // Re-throw the error so the calling action can handle it
      throw error;
    }
  }

  /**
   * Deletes a specific feature flag on the server via the Admin API.
   * Requires a valid session with admin privileges.
   * Invalidates the local storage cache on successful deletion.
   *
   * @param flagName - The name of the feature flag to delete.
   * @param session - The user's session object (must have admin rights).
   * @throws Will throw an error if the API call fails (e.g., not found).
   */
  async deleteFlag(flagName: string, session: Session): Promise<void> {
    logger.debug(
      `[FeatureFlagsService] Requesting deletion for flag '${flagName}' via Admin API.`
    );
    try {
      // Use the specific Admin endpoint for deleting (DELETE)
      // Expects a 204 No Content on success, so no specific response type needed
      await apiService.delete<void>( // Expect void response type
        API_CONFIG.ENDPOINTS.FEATURE_FLAGS.ADMIN_DELETE(flagName),
        session
      );

      logger.info(
        `[FeatureFlagsService] Successfully deleted flag '${flagName}' on the server. Invalidating cache.`
      );

      // Invalidate cache after successful server deletion
      this.clearCache();
    } catch (error) {
      logger.error(
        `[FeatureFlagsService] Failed to delete flag '${flagName}' on server via Admin API.`,
        error
      );
      // Re-throw the error so the calling action can handle it
      throw error;
    }
  }
}

// Export a singleton instance of the service
export const featureFlagsService = new FeatureFlagsService();
